import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Activity, FileBarChart, BookOpen, RefreshCcw, ShieldCheck, Star, Check, ArrowRight, Heart, Menu, X, Lock, CreditCard, Clock, Users, TrendingUp, MessageCircle, Brain, Target, ChevronRight, Home, BarChart3, Book, User, Play, Flame, Microscope, ExternalLink, Puzzle, Zap, BookOpenCheck, MessagesSquare, Handshake, GraduationCap, Video, FileText, ClipboardList, Presentation, Compass, CheckSquare, Users2, Stethoscope, ChevronDown, HelpCircle, LogIn, UserPlus, Sun, Moon } from "lucide-react";
import { createPortal } from "react-dom";
import GoogleOneTap from "@/components/GoogleOneTap";
import MobileMenuDrawer from "@/components/MobileMenuDrawer";
import Reveal, { WordStagger } from "@/components/Reveal";
import MegaNav, { MOBILE_DRAWER_ITEMS } from "@/components/MegaNav";
import SiteFooter from "@/components/SiteFooter";
import useAppReady from "@/lib/useAppReady";
import { useScrollReveal, useScrollProgressBar } from "@/lib/useScrollEffects";
import SEO from "@/components/SEO";
import PhoneMockup from "./landing/PhoneMockup";
import ResearchSection from "./landing/ResearchSection";
import ParentHelpSection from "./landing/ParentHelpSection";
import SupportSection from "./landing/SupportSection";
import ReviewsMarquee from "./landing/ReviewsMarquee";
import ResourcesSection from "./landing/ResourcesSection";
import FaqSection from "./landing/FaqSection";
import { ReportInsightsPhone, SuggestedActivitiesPhone } from "./landing/ReportPhones";
import FreeScreeningPopup from "@/components/FreeScreeningPopup";
import HowItWorksDialog from "@/components/HowItWorksDialog";
import SampleReportDialog from "@/components/SampleReportDialog";
import YouTubeFacade from "@/components/YouTubeFacade";

const LOGO = "/assets/images/kiddo-logo.png";
const WORDMARK = "/assets/images/kiddo-wordmark.png";

const FEATURES = [
  { icon: Activity,    title: "Holistic Assessment", body: "8-domain developmental screening built with paediatric therapists.",        accent: "from-coral",       haloBg: "bg-coral/10",       iconColor: "text-coral" },
  { icon: Sparkles,    title: "Daily Activities",    body: "Therapist-curated play & learning, tailored to your child each day.",      accent: "from-emerald-500", haloBg: "bg-emerald-100",    iconColor: "text-emerald-500" },
  { icon: FileBarChart,title: "Progress Reports",    body: "Beautiful, plain-language insights you can act on.",                        accent: "from-violet-500",  haloBg: "bg-violet-100",     iconColor: "text-violet-500" },
  { icon: BookOpen,    title: "Expert Courses",      body: "Bite-sized lessons from leading child specialists.",                        accent: "from-amber-500",   haloBg: "bg-amber-100",      iconColor: "text-amber-500" },
  { icon: RefreshCcw,  title: "30-Day Cycles",       body: "Reassess every month — celebrate real growth.",                             accent: "from-pink-500",    haloBg: "bg-pink-100",       iconColor: "text-pink-500" },
  { icon: ShieldCheck, title: "Therapist Verified",  body: "Get reports reviewed by certified professionals.",                          accent: "from-sky-500",     haloBg: "bg-sky-100",        iconColor: "text-sky-500" },
];

const STEPS = [
  { n: "01", icon: User,         title: "Create Account",       body: "Sign up free — parent or therapist.",                   accent: "from-coral",       haloBg: "bg-coral/10",      iconColor: "text-coral" },
  { n: "02", icon: Heart,        title: "Add Child Profile",    body: "Quick guided wizard, under 5 minutes.",                 accent: "from-emerald-500", haloBg: "bg-emerald-100",   iconColor: "text-emerald-500" },
  { n: "03", icon: Activity,     title: "Complete Assessment",  body: "8 domains, friendly color-coded scale.",                accent: "from-violet-500",  haloBg: "bg-violet-100",    iconColor: "text-violet-500" },
  { n: "04", icon: Target,       title: "Get Action Plan",      body: "Detailed report + daily activities you can do today.",  accent: "from-amber-500",   haloBg: "bg-amber-100",     iconColor: "text-amber-500" },
];

// "Built by Experts. Driven by Impact." — replaces the old TESTIMONIALS block.
// Each card highlights one credibility pillar.
const BUILDER_PILLARS = [
  {
    icon: Brain,
    title: "Founded by Occupational Therapists",
    body: "Kiddo+ was created by Dr. Dipak Kumar and Dr. Sarmista Pradhan, Occupational Therapists with experience supporting children with autism, ADHD, sensory processing challenges, developmental delays, and other neurodivergent needs.",
    accent: "from-coral",
    haloBg: "bg-coral/10",
    iconColor: "text-coral",
  },
  {
    icon: BookOpenCheck,
    title: "Evidence-Based Guidance",
    body: "Every recommendation, activity, and strategy inside Kiddo+ is grounded in child development science, occupational therapy principles, and real-world clinical experience.",
    accent: "from-violet-500",
    haloBg: "bg-violet-100",
    iconColor: "text-violet-500",
  },
  {
    icon: Lock,
    title: "Privacy First",
    body: "Your child's information is protected with industry-standard security practices. We never sell personal data, and parents remain in control of their family's information.",
    accent: "from-sky-500",
    haloBg: "bg-sky-100",
    iconColor: "text-sky-500",
  },
  {
    icon: Heart,
    title: "Purpose-Led & Parent-Centered",
    body: "Kiddo+ was built to reduce the stress, confusion, and isolation many parents experience when supporting a child with developmental challenges. Our goal is to provide practical guidance that families can use every day.",
    accent: "from-pink-500",
    haloBg: "bg-pink-100",
    iconColor: "text-pink-500",
  },
];

const PRICING = [
  { name: "Free", price: "₹0", interval: "forever", desc: "Get started",
    features: ["1 child profile", "Full 8-domain assessment", "5 activities to try", "Basic report"], cta: "Get Started",
    accent: "from-emerald-500", haloBg: "bg-emerald-100", iconColor: "text-emerald-500", icon: Heart },
  { name: "Pro Parent", price: "₹299", originalPrice: "₹999", interval: "/month", featured: true, desc: "Most chosen by parents",
    features: ["Everything in Free", "Personalized daily activities", "Detailed report with therapist verification", "See measurable progress every month", "Focus Areas sub-skill assessments", "PDF report download"], cta: "Upgrade to Pro",
    accent: "from-coral", haloBg: "bg-coral/10", iconColor: "text-coral", icon: Sparkles },
  { name: "Therapist", price: "₹499", interval: "/child/month", desc: "For professionals",
    features: ["Detailed therapist tools", "Bulk reports", "Priority support", "Verified badge", "Therapist notes field"], cta: "Join as Therapist",
    accent: "from-violet-500", haloBg: "bg-violet-100", iconColor: "text-violet-500", icon: ShieldCheck },
];

// Press strip — horizontal infinite marquee. Each logo is grayscale until
// hovered (then shows its native colour). Click opens the source in a new tab.
const PRESS_OUTLETS = [
  { name: "Dailyhunt", src: "/assets/images/press-dailyhunt.png", url: "https://m.dailyhunt.in/news/india/english/thebusinessstories-epaper-dh81e4e6e2104e4dbbb7f32ee26194cfb7/-newsid-dh81e4e6e2104e4dbbb7f32ee26194cfb7_3441fb91088b48f6851748a6317e6150?sm=Y" },
  { name: "The Business Stories", src: "/assets/images/press-tbs.png", url: "https://thebusinessstories.com/occupational-therapists-built-kiddo-to-give-parents-personalized-child-development-guidance-for-free/" },
  { name: "The Entrepreneur Story", src: "/assets/images/press-tes.png", url: "https://theentrepreneurstories.com/occupational-therapists-built-kiddo-to-give-parents-personalized-child-development-guidance-for-free/" },
  { name: "The Hindustan Wire", src: "/assets/images/press-thw.png", url: "https://thehindustanwires.com/occupational-therapists-built-kiddo-to-give-parents-personalized-child-development-guidance-for-free/" },
];

const AVATAR_EMOJI = ["🧒🏽", "👧🏻", "👦🏽", "🧑🏽"];

// Small white feature chips beneath the hero CTAs.
const HERO_TRUST_TILES = [
  { icon: Clock, color: "text-mint", title: "Personalized", line: "in 5 minutes" },
  { icon: Users, color: "text-plum", title: "Designed by", line: "therapists" },
  { icon: TrendingUp, color: "text-coral", title: "Track progress", line: "week by week" },
  { icon: Heart, color: "text-pink-400", title: "Free", line: "to start" },
];

// Reusable parent avatar URLs for the social-proof row (uploaded portraits).
// Clicking any one scrolls to the full parent stories marquee below.
const HERO_PARENT_AVATARS = [
  { src: "/assets/images/avatar-neha-joshi.png",     name: "Neha Joshi" },
  { src: "/assets/images/avatar-megha-patel.png",    name: "Megha Patel" },
  { src: "/assets/images/avatar-karthik-iyer.png",   name: "Karthik Iyer" },
  { src: "/assets/images/avatar-arjun-reddy.png",    name: "Arjun Reddy" },
  { src: "/assets/images/avatar-ananya-nair.png",    name: "Ananya Nair" },
];







export default function Landing() {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Controlled state for the two marketing popups added in the Feb 2026
  // landing-polish batch: (a) HowItWorksDialog reached from the hero
  // "See how it works" button, (b) SampleReportDialog reached from the
  // new "View Sample Report" button in the Your Report section.
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [sampleReportOpen, setSampleReportOpen] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();
  useAppReady();
  // Reveal-on-scroll + scroll-progress hairline + sticky CTA — both effects
  // live in /app/frontend/src/lib/useScrollEffects.js so Landing.jsx stays
  // focused on layout. See those hooks for behaviour details.
  useScrollReveal();
  useScrollProgressBar();

  return (
    <div className="min-h-screen bg-cream text-inkdark">
      <FreeScreeningPopup />
      <SEO
        title="Kiddo+ — Free Child Development Assessment & Daily Therapy Activities"
        description="Free 5-minute developmental assessment + personalised therapy activities every day for kids 3 to 12. Built by occupational therapists. No credit card."
        path="/"
        keywords="child development assessment,therapy activities for kids, occupational therapy at home, online ocuupational therapy, autism activities, ADHD support, sensory processing, parenting toolkit, parenting advice, activities for kids at home, child development activities, occupational therapy activities, occupational therapist for children, handwriting activities, handwriting activities, speech therapy activities, autism activities, free developmental assessment, online developmental assessment, child development app, therapy activities for kids, home therapy program, "
      />
      {/* Phase 5 — Skip-to-main accessibility link */}
      <a href="#main-content" className="kpp-skip-link">Skip to main content</a>

      {/* Phase 4 — Scroll progress hairline */}
      <div id="kpp-scroll-bar" className="kpp-scroll-progress" aria-hidden="true" />

      {/* One-Tap Google sign-in prompt (no UI — Google renders its own widget) */}
      <GoogleOneTap />
      {/* Header */}
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="landing-logo">
            <img src={LOGO} alt="Kiddo+" className="w-9 h-9 sm:w-10 sm:h-10 rounded-[22%]" />
            <img src={WORDMARK} alt="Kiddo+" className="h-7 sm:h-8 w-auto" />
          </Link>
          <MegaNav />
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
              className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-coral/10 text-inkmid hover:text-coral transition-colors"
              data-testid="landing-theme-toggle"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <Link to="/login" className="text-sm font-bold text-inkmid hover:text-coral px-3 py-2 hidden md:inline-block" data-testid="header-login-btn">Log in</Link>
            <Link to="/signup" className="hidden md:inline-block">
              <Button className="kpp-focus-ring bg-coral hover:bg-coral-dark text-white rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold shadow-soft-md whitespace-nowrap" data-testid="header-cta-btn">
                Get Started <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1" />
              </Button>
            </Link>
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-coral/10 text-inkdark active:scale-95 transition"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <MobileMenuDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={null}
        brand={{ logo: LOGO, wordmark: WORDMARK }}
        items={MOBILE_DRAWER_ITEMS.filter((it) => it.to !== "/")}
        footer={
          <div className="space-y-2">
            <Link to="/signup" onClick={() => setMobileOpen(false)} className="block">
              <Button className="w-full bg-coral hover:bg-coral-dark text-white rounded-full font-bold py-5" data-testid="mobile-menu-signup">
                <UserPlus className="w-4 h-4 mr-1.5" /> Get started — free
              </Button>
            </Link>
            <Link to="/login" onClick={() => setMobileOpen(false)} className="block">
              <Button
                variant="outline"
                className="w-full rounded-full border-2 border-coral/30 text-coral hover:bg-coral/10 hover:border-coral font-bold py-5 bg-transparent"
                data-testid="mobile-menu-login"
              >
                <LogIn className="w-4 h-4 mr-1.5" /> Log in
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => { setMobileOpen(false); toggleTheme(); }}
              className="w-full rounded-full border-2 border-plum/10 text-inkmid hover:bg-plum/5 font-semibold py-4"
              data-testid="mobile-menu-theme-toggle"
            >
              {theme === "dark" ? <Sun className="w-4 h-4 mr-1.5" /> : <Moon className="w-4 h-4 mr-1.5" />}
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </Button>
          </div>
        }
      />

      {/* HERO */}
      <section id="main-content" className="relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-coral/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-mint/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-12 pb-16 lg:pt-20 lg:pb-28 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div className="animate-fade-up text-center lg:text-left">
            <div className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-gold/25 text-[#8b6900] rounded-full text-xs font-bold mb-6 shadow-sm">
              <Star className="w-3 h-3 fill-current" /> Built with therapists &amp; parents
            </div>
            <h1 className="font-heading text-[2.5rem] sm:text-5xl lg:text-[3.75rem] xl:text-[4.25rem] leading-[1.05] tracking-tight text-inkdark">
              <WordStagger startIndex={0}>Get Personalized</WordStagger><br />
              <span className="text-coral"><WordStagger startIndex={2}>Activities &amp; Guidance</WordStagger></span><br />
              <WordStagger startIndex={5}>for Your Child</WordStagger>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-inkmid leading-relaxed max-w-xl mx-auto lg:mx-0">
              Answer a few simple questions and receive therapist-designed activities tailored to your child&apos;s communication, learning, attention, and developmental needs.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
              <Link to="/signup" className="w-full sm:w-auto">
                <Button className="kpp-glow-pulse kpp-focus-ring w-full sm:w-auto bg-coral hover:bg-coral-dark text-white rounded-full px-7 py-6 font-bold text-base shadow-soft-md hover-lift" data-testid="hero-start-free-btn">
                  Get My Child&apos;s Free Plan <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <a href="#how" className="w-full sm:w-auto">
                <Button
                  variant="outline"
                  onClick={(e) => {
                    // Open the walkthrough popup; anchor scroll remains a
                    // secondary fallback for keyboard/no-JS users so we
                    // don't preventDefault unconditionally.
                    e.preventDefault();
                    setHowItWorksOpen(true);
                  }}
                  className="kpp-focus-ring w-full sm:w-auto rounded-full px-7 py-6 font-bold text-base border-2 border-plum/15 text-plum bg-white hover:bg-plum/5"
                  data-testid="hero-how-btn"
                >
                  <Play className="w-4 h-4 mr-2 fill-current" /> See how it works
                </Button>
              </a>
            </div>

            {/* Risk-reversal line */}
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1.5 text-xs sm:text-sm text-inkmid justify-center lg:justify-start" data-testid="hero-reassurance">
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> No diagnosis required</span>
              <span className="inline-flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> No credit card required</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Start free in under 5 minutes</span>
            </div>

            {/* 4 trust tiles */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto lg:mx-0">
              {HERO_TRUST_TILES.map((t) => (
                <div key={t.title} className="bg-white rounded-2xl p-4 border border-plum/5 shadow-soft text-center sm:text-left" data-testid={`hero-tile-${t.title.toLowerCase().replace(/\s/g,'-')}`}>
                  <t.icon className={`w-6 h-6 ${t.color} mb-2 mx-auto sm:mx-0`} strokeWidth={2.2} />
                  <p className="text-[13px] font-bold text-inkdark leading-tight">{t.title}</p>
                  <p className="text-xs text-inkmid leading-tight">{t.line}</p>
                </div>
              ))}
            </div>

            {/* Social proof */}
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 justify-center lg:justify-start">
              <Link
                to="/success-stories"
                className="flex -space-x-3 justify-center sm:justify-start group cursor-pointer focus:outline-none focus:ring-2 focus:ring-coral/40 rounded-full -m-1 p-1"
                aria-label="Read parent success stories"
                data-testid="hero-parent-stories-link"
              >
                {HERO_PARENT_AVATARS.map((p, i) => (
                  <img
                    key={`pavatar-${i}`}
                    src={p.src}
                    alt={p.name}
                    title={p.name}
                    loading="lazy"
                    className="w-11 h-11 rounded-full border-2 border-white shadow-soft object-cover bg-cream transition-transform duration-200 group-hover:-translate-y-0.5"
                  />
                ))}
              </Link>
              <div className="text-center sm:text-left">
                <Link
                  to="/success-stories"
                  className="inline-flex text-gold justify-center sm:justify-start gap-0.5 focus:outline-none focus:ring-2 focus:ring-coral/40 rounded-md"
                  aria-label="Read parent success stories"
                  data-testid="hero-rating-stars-link"
                >
                  {[...Array(5)].map((_,i)=><Star key={`hero-star-${i}`} className="w-4 h-4 fill-gold" />)}
                </Link>
                <p className="text-xs sm:text-sm text-inkdark font-semibold mt-0.5">
                  <Link
                    to="/success-stories"
                    className="hover:text-coral transition-colors"
                    data-testid="hero-rating-link"
                  >
                    <strong>4.9/5</strong> from parents and therapists
                  </Link>
                </p>
                <p className="text-[11px] sm:text-xs text-inkmid">Trusted by 1,280+ families</p>
              </div>
            </div>
          </div>

          {/* Phone mockup */}
          <div className="relative animate-float mx-auto w-full max-w-sm">
            <PhoneMockup />
          </div>
        </div>
      </section>

      {/* RECENTLY FEATURED IN — infinite marquee */}
      <section className="border-y border-plum/5 bg-cream" data-testid="press-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
          <p className="text-xs font-bold uppercase tracking-widest text-inkmid mb-5 text-center">As Seen In</p>
          <div className="kpp-marquee-mask overflow-hidden">
            <ul className="kpp-marquee-track flex items-center gap-10 sm:gap-16">
              {[...PRESS_OUTLETS, ...PRESS_OUTLETS].map((p, i) => (
                <li key={`${p.name}-${i}`} className="shrink-0">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Read about Kiddo+ on ${p.name}`}
                    title={p.name}
                    data-testid={`press-${p.name.toLowerCase().replace(/\s/g, '-')}`}
                    className="group block"
                  >
                    <img
                      src={p.src}
                      alt={p.name}
                      loading="lazy"
                      className="h-12 sm:h-14 w-auto max-w-[160px] sm:max-w-[200px] object-contain grayscale brightness-[0.55] opacity-80 transition-all duration-300 group-hover:grayscale-0 group-hover:brightness-100 group-hover:opacity-100 group-hover:scale-[1.04]"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PARENTING MADE EASIER — research-backed comparison */}
      <ResearchSection />

      {/* CHANGE HOW PARENTING FEELS — 3 flip cards */}
      <ParentHelpSection />

      {/* WHO WE SUPPORT */}
      <SupportSection />

      {/* FEATURES — Why Kiddo+ */}
      <section id="features" className="relative bg-white kpp-blend-top-cream py-20 lg:py-24 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute -top-16 right-1/4 w-48 h-48 bg-coral/10 rounded-full blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 -left-16 w-56 h-56 bg-violet-100 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14 kpp-reveal" data-reveal>
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-coral/10 text-coral rounded-full text-[11px] font-bold uppercase tracking-widest mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Why Kiddo+
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-inkdark">
              A Clear <span className="text-coral block sm:inline">Development Plan</span> For Your Child.
            </h2>
            <p className="mt-5 text-inkmid text-sm sm:text-base leading-relaxed">
              Everything you need to understand strengths, support challenges, and track progress over time.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="relative bg-white rounded-2xl shadow-soft border border-plum/5 px-3 py-5 sm:py-6 flex flex-col items-center text-center hover-lift overflow-hidden"
                data-testid={`feature-${f.title.toLowerCase().replace(/\s/g,'-')}`}
                title={f.title}
                style={{ "--i": i }}
              >
                <span className={`absolute top-0 left-4 right-4 h-1 rounded-b-full bg-gradient-to-r ${f.accent} to-transparent`} />
                <div className={`kpp-icon-hover w-14 h-14 rounded-full ${f.haloBg} flex items-center justify-center mb-3 mt-1`}>
                  <f.icon className={`w-6 h-6 ${f.iconColor}`} strokeWidth={2.2} />
                </div>
                <p className="font-heading text-sm sm:text-base leading-tight text-inkdark">{f.title}</p>
                <p className="hidden sm:block text-[11px] text-inkmid leading-snug mt-1.5">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — sits between Why Kiddo+ and Parent Stories */}
      <section id="how" className="relative bg-white py-20 lg:py-24 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute -top-12 -right-12 w-56 h-56 bg-mint/15 rounded-full blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 -left-12 w-48 h-48 bg-coral/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14 kpp-reveal" data-reveal>
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-mint/15 text-mint-dark rounded-full text-[11px] font-bold uppercase tracking-widest mb-5">
              <Sparkles className="w-3.5 h-3.5" /> How It Works
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-inkdark">
              From signup to action plan <span className="text-coral block sm:inline">in 10 minutes.</span>
            </h2>
            <p className="mt-5 text-inkmid text-sm sm:text-base leading-relaxed">
              Four guided steps — and you&apos;re walking away with a personalised plan you can use today.
            </p>
          </div>

          {/* Walk-through video, click-to-play facade. See
              YouTubeFacade.jsx — we show a static thumbnail + play button
              on first paint so the iframe (and its ~500KB of JS) never
              costs us LCP. Tapping play mounts the real iframe with
              autoplay + mute + Full-HD hints. Same video ID as the hero
              popup so a future asset swap is a one-liner. */}
          <div className="max-w-3xl mx-auto mb-12 kpp-reveal" data-reveal>
            <YouTubeFacade
              videoId="BodoPoqBf68"
              title="How Kiddo+ works"
              testId="how-it-works-inline-video"
            />
          </div>

          <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 kpp-reveal" data-reveal data-stagger style={{ "--i": 0 }}>
            {/* Animated connector line on desktop only */}
            <div aria-hidden="true" className="hidden lg:block absolute top-1/2 left-[12%] right-[12%] h-px border-t-2 border-dashed border-coral/30 kpp-connector" />

            {STEPS.map((s, i) => (
              <div
                key={s.n}
                className="relative bg-white rounded-2xl shadow-soft border border-plum/5 px-3 py-5 sm:py-6 flex flex-col items-center text-center hover-lift overflow-hidden group"
                data-testid={`step-${s.n}`}
                style={{ "--i": i }}
              >
                <span className={`absolute top-0 left-4 right-4 h-1 rounded-b-full bg-gradient-to-r ${s.accent} to-transparent`} />
                <span className="absolute top-3 right-4 text-[11px] font-bold tracking-widest text-inkmid/60">{s.n}</span>
                <div className={`kpp-icon-hover w-14 h-14 rounded-full ${s.haloBg} flex items-center justify-center mb-3 mt-1`}>
                  <s.icon className={`w-6 h-6 ${s.iconColor}`} strokeWidth={2.2} />
                </div>
                <p className="font-heading text-sm sm:text-base leading-tight text-inkdark">{s.title}</p>
                <p className="text-[11px] text-inkmid leading-snug mt-1.5">{s.body}</p>
              </div>
            ))}
          </div>

          {/* Centered CTA below the 4 steps */}
          <div className="mt-12 flex flex-col items-center gap-3">
            <Link to="/signup">
              <Button className="kpp-focus-ring bg-coral hover:bg-coral-dark text-white rounded-full px-8 py-6 font-bold text-base shadow-soft-md hover-lift" data-testid="how-cta-btn">
                Get started for free <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <p className="text-xs text-inkmid">No credit card required · Start in under 5 minutes</p>
          </div>
        </div>
      </section>

      {/* PARENT REVIEWS — You're Not Alone */}
      <ReviewsMarquee />

      {/* APP PREVIEW — A report you'll actually want to read */}
      <section id="report-preview" className="relative bg-white py-20 lg:py-24 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute -top-12 -left-12 w-56 h-56 bg-mint/15 rounded-full blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 -right-12 w-64 h-64 bg-coral/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* LEFT — copy */}
          <div className="text-center lg:text-left kpp-reveal" data-reveal>
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-mint/15 text-mint-dark rounded-full text-[11px] font-bold uppercase tracking-widest mb-5">
              <FileBarChart className="w-3.5 h-3.5" /> Your Report
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-inkdark">
              A report you&apos;ll actually <span className="text-coral block sm:inline">want to read.</span>
            </h2>
            <p className="mt-5 text-inkmid text-sm sm:text-base leading-relaxed max-w-md mx-auto lg:mx-0">
              Beautiful, plain-language insights generated for your specific child. No clinical jargon — just clarity and the next steps you can take today.
            </p>
            <ul className="mt-7 space-y-2.5 text-left max-w-md mx-auto lg:mx-0">
              {["Colour-coded 8-domain scores","Therapist-curated home suggestions","30-day activity action plan","Therapist verification"].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-inkdark">
                  <Check className="w-5 h-5 text-mint mt-0.5 shrink-0" />
                  <span className="text-sm sm:text-base">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center lg:justify-start">
              <Link to="/signup">
                <Button className="kpp-focus-ring w-full sm:w-auto bg-coral hover:bg-coral-dark text-white rounded-full px-8 py-6 font-bold shadow-soft-md hover-lift" data-testid="preview-cta-btn">
                  Try Free <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={() => setSampleReportOpen(true)}
                data-testid="preview-sample-report-btn"
                className="kpp-focus-ring w-full sm:w-auto rounded-full px-8 py-6 font-bold border-2 border-plum/15 text-plum bg-white hover:bg-plum/5"
              >
                <FileText className="w-4 h-4 mr-2" /> View Sample Report
              </Button>
            </div>
          </div>

          {/* RIGHT — two iPhone-style mockups, floating */}
          <div className="relative mx-auto w-full max-w-md lg:max-w-none lg:pl-4 kpp-reveal" data-reveal>
            <div className="grid grid-cols-2 gap-4 lg:gap-6">
              {/* Report insights phone */}
              <div className="kpp-float-a">
                <ReportInsightsPhone />
              </div>
              {/* Suggested activities phone */}
              <div className="kpp-float-b mt-10">
                <SuggestedActivitiesPhone />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BUILT BY EXPERTS — replaces old "Loved by parents & therapists" testimonials */}
      <section id="built-by-experts" className="relative bg-white py-16 lg:py-20 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute -top-12 left-1/4 w-56 h-56 bg-coral/10 rounded-full blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 w-64 h-64 bg-violet-100 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14 kpp-reveal" data-reveal>
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-violet-100 text-violet-700 rounded-full text-[11px] font-bold uppercase tracking-widest mb-5">
              <ShieldCheck className="w-3.5 h-3.5" /> Built by Experts
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-inkdark">
              Built by Experts. <span className="text-coral block sm:inline">Driven by Impact.</span>
            </h2>
            <p className="mt-5 text-inkmid text-sm sm:text-base leading-relaxed">
              We&apos;re committed to making high-quality child development support accessible, practical, and affordable for every family.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 kpp-reveal" data-reveal data-stagger>
            {BUILDER_PILLARS.map((p, i) => (
              <div
                key={p.title}
                className="relative bg-white rounded-3xl shadow-soft border border-plum/5 p-6 sm:p-7 flex gap-4 sm:gap-5 hover-lift overflow-hidden"
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s|&|,|\./g, '-').replace(/-+/g, '-')}`}
                style={{ "--i": i }}
              >
                <span className={`absolute top-0 left-6 right-6 h-1 rounded-b-full bg-gradient-to-r ${p.accent} to-transparent`} />
                <div className={`w-14 h-14 shrink-0 rounded-2xl ${p.haloBg} flex items-center justify-center mt-1`}>
                  <p.icon className={`w-7 h-7 ${p.iconColor}`} strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-lg sm:text-xl text-inkdark leading-tight mb-2">{p.title}</h3>
                  <p className="text-sm text-inkmid leading-relaxed">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="plans" className="relative bg-white py-20 lg:py-24 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute -top-12 -right-12 w-56 h-56 bg-mint/15 rounded-full blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 -left-12 w-48 h-48 bg-coral/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14 kpp-reveal" data-reveal>
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-mint/15 text-mint-dark rounded-full text-[11px] font-bold uppercase tracking-widest mb-5">
              <Heart className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} /> Simple, Kind Pricing
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-inkdark">
              Start free. <span className="text-coral block sm:inline">Upgrade when you&apos;re ready.</span>
            </h2>
            <p className="mt-5 text-inkmid text-sm sm:text-base leading-relaxed">
              No credit card needed. Cancel anytime. Pro Parent gives you the full set of therapist-curated tools.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto items-stretch kpp-reveal" data-reveal data-stagger>
            {PRICING.map((p, i) => (
              <div
                key={p.name}
                className={`group relative ${p.featured ? "md:mt-3" : ""} bg-white rounded-3xl shadow-soft border border-plum/5 px-6 py-7 sm:p-7 flex flex-col hover-lift kpp-tilt overflow-visible ${p.featured ? "md:scale-[1.03] md:-translate-y-1 ring-2 ring-coral/30" : ""}`}
                data-testid={`pricing-${p.name.toLowerCase().replace(/\s/g,'-')}`}
                style={{ "--i": i }}
              >
                <span className={`absolute top-0 left-6 right-6 h-1 rounded-b-full bg-gradient-to-r ${p.accent} to-transparent`} />
                {p.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 bg-coral text-white text-[11px] font-bold uppercase tracking-[0.12em] px-4 py-1.5 rounded-full shadow-[0_8px_20px_-6px_rgba(241,93,94,0.55)] whitespace-nowrap ring-4 ring-white" data-testid="pricing-popular-badge">★ Most Popular</div>
                )}
                <div className={`w-12 h-12 rounded-2xl ${p.haloBg} flex items-center justify-center mb-4 mt-1`}>
                  <p.icon className={`w-6 h-6 ${p.iconColor}`} strokeWidth={2.2} />
                </div>
                <h3 className="font-heading text-2xl text-inkdark mb-1">{p.name}</h3>
                <p className="text-sm text-inkmid mb-5">{p.desc}</p>

                <div className="mb-6 flex items-baseline gap-2 flex-wrap">
                  {p.originalPrice && (
                    <span className="font-heading text-lg text-inkmid line-through" data-testid={`pricing-original-${p.name.toLowerCase().replace(/\s/g,'-')}`}>{p.originalPrice}</span>
                  )}
                  <span className="font-heading text-4xl font-bold text-inkdark">{p.price}</span>
                  <span className="text-inkmid text-sm">{p.interval}</span>
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-inkdark">
                      <Check className="w-4 h-4 text-mint mt-0.5 shrink-0" />{f}
                    </li>
                  ))}
                </ul>

                <Link to="/signup" className="block">
                  <Button className={`kpp-focus-ring w-full rounded-full py-6 font-bold ${p.featured ? "bg-coral hover:bg-coral-dark text-white" : "bg-plum hover:bg-plum/90 text-white"}`}>{p.cta}</Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      {/* RESOURCES */}
      <ResourcesSection />

      {/* CTA STRIP */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="bg-gradient-to-br from-coral to-coral-dark rounded-[40px] p-8 sm:p-12 lg:p-16 text-center text-white shadow-soft-lg kpp-reveal" data-reveal>
            <Heart className="w-10 h-10 mx-auto mb-4 text-white/90" />
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl mb-4">Start your child&apos;s growth journey today</h2>
            <p className="text-white/90 text-base sm:text-lg mb-8 max-w-2xl mx-auto">Free assessment. No credit card needed.</p>
            <Link to="/signup" className="inline-block w-full sm:w-auto">
              <Button className="kpp-focus-ring w-full sm:w-auto bg-white text-coral hover:bg-cream rounded-full px-10 py-6 font-bold text-base shadow-soft-md hover-lift" data-testid="bottom-cta-btn">
                Start Free Assessment <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />

      {/* Phase 4 — Mobile sticky CTA (appears after hero) */}
      <div id="kpp-sticky-cta" className="kpp-sticky-cta md:hidden" aria-hidden="false">
        <Link to="/signup" className="block">
          <Button className="kpp-focus-ring w-full bg-coral hover:bg-coral-dark text-white rounded-full py-6 font-bold text-base shadow-soft-lg" data-testid="sticky-mobile-cta">
            <Sparkles className="w-4 h-4 mr-1" /> Start Free Today <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      {/* Marketing popups — mounted once at the root so the shadcn Portal
          escapes any transformed / clipping ancestors in the flow. */}
      <HowItWorksDialog open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />
      <SampleReportDialog open={sampleReportOpen} onOpenChange={setSampleReportOpen} />
    </div>
  );
}








